import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { AdminShell } from '../components/admin/AdminShell';
import {
  adminApi,
  ConsentTrendsResponse,
  PurposeBreakdownResponse,
  TemplateBreakdownResponse,
  ConversionMetricsResponse,
} from '../services/adminApi';
import { useRegion } from '../contexts/RegionContext';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Main container with gradient background
const PageContainer = styled.div`
  min-height: 100%;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #faf5ff 100%);
  padding: 0;
`;

const ContentWrapper = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 32px;
  animation: ${fadeIn} 0.4s ease;
`;

// Executive Header
const ExecutiveHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 20px;
`;

const HeaderLeft = styled.div``;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
`;

const PageSubtitle = styled.p`
  font-size: 15px;
  color: #64748b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RegionBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  border-radius: 20px;
  font-weight: 600;
  font-size: 13px;
`;

const HeaderControls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const DateRangeSelect = styled.select`
  padding: 12px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #6366f1;
  }
  
  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
`;

const ExportButton = styled.button`
  padding: 12px 24px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.25);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.35);
  }
`;

// Executive KPI Cards
const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 32px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const KPICard = styled.div<{ $gradient: string }>`
  background: white;
  border-radius: 20px;
  padding: 28px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$gradient};
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
  }
`;

const KPIIcon = styled.div<{ $color: string }>`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: ${props => props.$color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 16px;
`;

const KPILabel = styled.div`
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const KPIValue = styled.div`
  font-size: 36px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
  margin-bottom: 8px;
`;

const KPITrend = styled.div<{ $positive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  background: ${props => props.$positive ? '#dcfce7' : '#fef2f2'};
  color: ${props => props.$positive ? '#16a34a' : '#dc2626'};
`;

// Chart Section
const ChartsSection = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ChartBadge = styled.span`
  font-size: 11px;
  padding: 4px 10px;
  background: #f1f5f9;
  border-radius: 6px;
  color: #64748b;
  font-weight: 600;
`;

// Status Distribution
const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const DonutCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;

const DonutValue = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
`;

const DonutLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
`;

// Legend Items
const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 12px;
  transition: all 0.2s;
  
  &:hover {
    background: #f1f5f9;
  }
`;

const LegendLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LegendDot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 4px;
  background: ${props => props.$color};
`;

const LegendText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #475569;
`;

const LegendValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
`;

// Loading State
const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

// Empty State
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 250px;
  color: #94a3b8;
  
  span {
    font-size: 56px;
    margin-bottom: 16px;
  }
  
  p {
    font-size: 15px;
    margin: 0;
    font-weight: 500;
  }
`;

// Status Colors
const STATUS_COLORS: Record<string, string> = {
  'ACCEPTED': '#22c55e',
  'COMPLETED': '#22c55e',
  'PENDING': '#f59e0b',
  'WITHDRAWN': '#ef4444',
  'REJECTED': '#ef4444',
  'EXPIRED': '#94a3b8',
};

export const Analytics: React.FC = () => {
  const { selectedRegion } = useRegion();
  const [dateRange, setDateRange] = useState<number>(30);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  
  // Data state
  const [trends, setTrends] = useState<ConsentTrendsResponse | null>(null);
  const [statusBreakdown, setStatusBreakdown] = useState<PurposeBreakdownResponse | null>(null);
  const [templateBreakdown, setTemplateBreakdown] = useState<TemplateBreakdownResponse | null>(null);
  const [conversionMetrics, setConversionMetrics] = useState<ConversionMetricsResponse | null>(null);

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Pass region filter to API for data residency compliance
      const regionFilter = selectedRegion.code;
      const [trendsData, statusData, templateData, conversionData] = await Promise.all([
        adminApi.getConsentTrends(dateRange, regionFilter).catch(() => null),
        adminApi.getConsentByPurpose(regionFilter).catch(() => null),
        adminApi.getConsentByTemplate(regionFilter).catch(() => null),
        adminApi.getConversionMetrics(regionFilter).catch(() => null),
      ]);
      
      setTrends(trendsData);
      setStatusBreakdown(statusData);
      setTemplateBreakdown(templateData);
      setConversionMetrics(conversionData);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedRegion]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleExport = () => {
    const csvRows = [
      ['Metric', 'Value'],
      ['Total Consents', conversionMetrics?.total || 0],
      ['Completed Consents', conversionMetrics?.completed || 0],
      ['Pending Consents', conversionMetrics?.pending || 0],
      ['Rejected Consents', conversionMetrics?.rejected || 0],
      ['Conversion Rate', conversionMetrics?.conversionRate || 'N/A'],
    ];
    
    if (trends?.trend) {
      csvRows.push(['', ''], ['Date', 'Consents']);
      trends.trend.forEach(t => csvRows.push([t.date, t.count]));
    }
    
    const blob = new Blob([csvRows.map(row => row.join(',')).join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consent-analytics-${selectedRegion.code}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Format trend data for charts
  const trendData = trends?.trend?.map(t => ({
    date: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    count: t.count,
  })) || [];

  // Format status data for pie chart
  const statusData = statusBreakdown?.breakdown?.map(b => ({
    name: b.status || 'Unknown',
    value: b.count,
    color: STATUS_COLORS[b.status || ''] || '#94a3b8',
  })) || [];

  const totalStatusCount = statusData.reduce((sum, item) => sum + item.value, 0);

  return (
    <AdminShell title="Analytics">
      <PageContainer>
        <ContentWrapper>
          {/* Executive Header */}
          <ExecutiveHeader>
            <HeaderLeft>
              <PageTitle>📊 Analytics Dashboard</PageTitle>
              <PageSubtitle>
                Consent performance insights
                <RegionBadge $color={selectedRegion.color}>
                  {selectedRegion.flag} {selectedRegion.name}
                </RegionBadge>
              </PageSubtitle>
            </HeaderLeft>
            <HeaderControls>
              <DateRangeSelect 
                value={dateRange} 
                onChange={(e) => setDateRange(Number(e.target.value))}
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last 12 months</option>
              </DateRangeSelect>
              <ExportButton onClick={handleExport}>
                📥 Export Report
              </ExportButton>
            </HeaderControls>
          </ExecutiveHeader>

          {/* KPI Cards */}
          <KPIGrid>
            <KPICard $gradient="linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)">
              <KPIIcon $color="#6366f1">📋</KPIIcon>
              <KPILabel>Total Consents</KPILabel>
              <KPIValue>{loading ? '...' : (conversionMetrics?.total || 0).toLocaleString()}</KPIValue>
              <KPITrend $positive>↑ All time</KPITrend>
            </KPICard>
            
            <KPICard $gradient="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)">
              <KPIIcon $color="#22c55e">✓</KPIIcon>
              <KPILabel>Accepted</KPILabel>
              <KPIValue>{loading ? '...' : (conversionMetrics?.completed || statusData.find(s => s.name === 'ACCEPTED')?.value || 0).toLocaleString()}</KPIValue>
              {!loading && conversionMetrics && conversionMetrics.total > 0 && (
                <KPITrend $positive>
                  {((statusData.find(s => s.name === 'ACCEPTED')?.value || 0) / totalStatusCount * 100).toFixed(0)}% rate
                </KPITrend>
              )}
            </KPICard>
            
            <KPICard $gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">
              <KPIIcon $color="#f59e0b">⏳</KPIIcon>
              <KPILabel>Pending</KPILabel>
              <KPIValue>{loading ? '...' : (conversionMetrics?.pending || statusData.find(s => s.name === 'PENDING')?.value || 0).toLocaleString()}</KPIValue>
              <KPITrend>Awaiting action</KPITrend>
            </KPICard>
            
            <KPICard $gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)">
              <KPIIcon $color="#ef4444">↩</KPIIcon>
              <KPILabel>Withdrawn</KPILabel>
              <KPIValue>{loading ? '...' : (statusData.find(s => s.name === 'WITHDRAWN')?.value || 0).toLocaleString()}</KPIValue>
              <KPITrend>Revoked consents</KPITrend>
            </KPICard>
          </KPIGrid>

          {/* Main Charts */}
          <ChartsSection>
            <ChartCard>
              <ChartHeader>
                <ChartTitle>
                  📈 Consent Collection Trend
                  <ChartBadge>{dateRange} days</ChartBadge>
                </ChartTitle>
              </ChartHeader>
              {loading ? (
                <LoadingOverlay>
                  <LoadingSpinner />
                  <LoadingText>Loading trend data...</LoadingText>
                </LoadingOverlay>
              ) : trendData.length === 0 || trendData.every(d => d.count === 0) ? (
                <EmptyState>
                  <span>📊</span>
                  <p>No consent data for this period</p>
                </EmptyState>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0.05}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12, fill: '#94a3b8' }}
                      tickLine={false}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: '#94a3b8' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                        padding: '12px 16px',
                      }}
                      labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#6366f1" 
                      strokeWidth={3}
                      fillOpacity={1} 
                      fill="url(#colorGradient)" 
                      name="Consents"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard>
              <ChartHeader>
                <ChartTitle>🎯 Consent Status</ChartTitle>
              </ChartHeader>
              {loading ? (
                <LoadingOverlay>
                  <LoadingSpinner />
                </LoadingOverlay>
              ) : statusData.length === 0 ? (
                <EmptyState>
                  <span>🥧</span>
                  <p>No status data</p>
                </EmptyState>
              ) : (
                <>
                  <div style={{ position: 'relative', height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={85}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {statusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            background: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <DonutCenter>
                      <DonutValue>{totalStatusCount}</DonutValue>
                      <DonutLabel>Total</DonutLabel>
                    </DonutCenter>
                  </div>
                  <LegendContainer>
                    {statusData.map((item) => (
                      <LegendItem key={item.name}>
                        <LegendLabel>
                          <LegendDot $color={item.color} />
                          <LegendText>{item.name}</LegendText>
                        </LegendLabel>
                        <LegendValue>{item.value}</LegendValue>
                      </LegendItem>
                    ))}
                  </LegendContainer>
                </>
              )}
            </ChartCard>
          </ChartsSection>

          {/* Template Performance */}
          <StatusGrid>
            <ChartCard>
              <ChartHeader>
                <ChartTitle>📋 Top Templates</ChartTitle>
              </ChartHeader>
              {loading ? (
                <LoadingOverlay>
                  <LoadingSpinner />
                </LoadingOverlay>
              ) : !templateBreakdown?.topTemplates?.length ? (
                <EmptyState>
                  <span>📋</span>
                  <p>No template data</p>
                </EmptyState>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={templateBreakdown.topTemplates.slice(0, 5).map((t, i) => ({
                      name: (t.templateId || `Template ${i + 1}`).replace('tmpl-', '').replace(/-/g, ' ').toUpperCase(),
                      count: t.count,
                    }))}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 12, fill: '#94a3b8' }} />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tick={{ fontSize: 12, fill: '#475569', fontWeight: 500 }}
                      width={100}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                      }}
                    />
                    <Bar dataKey="count" fill="#6366f1" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard>
              <ChartHeader>
                <ChartTitle>⚡ Quick Metrics</ChartTitle>
              </ChartHeader>
              <LegendContainer>
                <LegendItem>
                  <LegendLabel>
                    <LegendDot $color="#6366f1" />
                    <LegendText>Total Templates</LegendText>
                  </LegendLabel>
                  <LegendValue>{loading ? '...' : templateBreakdown?.totalTemplates ?? 0}</LegendValue>
                </LegendItem>
                <LegendItem>
                  <LegendLabel>
                    <LegendDot $color="#8b5cf6" />
                    <LegendText>Total Consents</LegendText>
                  </LegendLabel>
                  <LegendValue>{loading ? '...' : conversionMetrics?.total ?? 0}</LegendValue>
                </LegendItem>
                <LegendItem>
                  <LegendLabel>
                    <LegendDot $color="#22c55e" />
                    <LegendText>Acceptance Rate</LegendText>
                  </LegendLabel>
                  <LegendValue>{loading ? '...' : conversionMetrics?.conversionRate || '0%'}</LegendValue>
                </LegendItem>
                <LegendItem style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
                  <LegendLabel>
                    <LegendDot $color="#f59e0b" />
                    <LegendText style={{ fontWeight: 700 }}>Conversion Rate</LegendText>
                  </LegendLabel>
                  <LegendValue style={{ color: '#d97706', fontSize: 20 }}>
                    {loading ? '...' : conversionMetrics?.conversionRate || '0%'}
                  </LegendValue>
                </LegendItem>
                <LegendItem>
                  <LegendLabel>
                    <LegendDot $color="#94a3b8" />
                    <LegendText>Avg. Completion Time</LegendText>
                  </LegendLabel>
                  <LegendValue>
                    {loading ? '...' : conversionMetrics?.avgCompletionTimeMs
                      ? `${Math.round(conversionMetrics.avgCompletionTimeMs / 1000 / 60)} min`
                      : 'N/A'
                    }
                  </LegendValue>
                </LegendItem>
              </LegendContainer>
            </ChartCard>
          </StatusGrid>

          {/* Compliance Health Section */}
          <div style={{ marginBottom: 32 }}>
            <ChartHeader>
              <ChartTitle>🛡️ Compliance Health</ChartTitle>
            </ChartHeader>
            <KPIGrid>
              <KPICard $gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">
                <KPIIcon $color="#f59e0b">⏰</KPIIcon>
                <KPILabel>Expiring Soon</KPILabel>
                <KPIValue>
                  {loading ? '...' : (() => {
                    const expiredCount = statusData.find(s => s.name === 'EXPIRED')?.value || 0;
                    // Show expired count as a proxy for expiring-soon; exact 30-day window requires consent-level data
                    return expiredCount;
                  })()}
                </KPIValue>
                <KPITrend>
                  {!loading && conversionMetrics && conversionMetrics.total > 0
                    ? `${((statusData.find(s => s.name === 'EXPIRED')?.value || 0) / conversionMetrics.total * 100).toFixed(1)}% of total`
                    : 'Within 30 days'}
                </KPITrend>
              </KPICard>

              <KPICard $gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)">
                <KPIIcon $color="#ef4444">↩</KPIIcon>
                <KPILabel>Withdrawal Rate</KPILabel>
                <KPIValue>
                  {loading ? '...' : (statusData.find(s => s.name === 'WITHDRAWN')?.value || 0)}
                </KPIValue>
                <KPITrend>
                  {!loading && conversionMetrics && conversionMetrics.total > 0
                    ? `${((statusData.find(s => s.name === 'WITHDRAWN')?.value || 0) / conversionMetrics.total * 100).toFixed(1)}% rate`
                    : 'No withdrawals'}
                </KPITrend>
              </KPICard>

              <KPICard $gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)">
                <KPIIcon $color="#8b5cf6">🔄</KPIIcon>
                <KPILabel>Reconsent Completion</KPILabel>
                <KPIValue>
                  {loading ? '...' : 'N/A'}
                </KPIValue>
                <KPITrend>Coming soon</KPITrend>
              </KPICard>

              <KPICard $gradient="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)">
                <KPIIcon $color="#22c55e">✓</KPIIcon>
                <KPILabel>Compliance Score</KPILabel>
                <KPIValue>
                  {loading ? '...' : (() => {
                    const total = conversionMetrics?.total || 0;
                    const withdrawn = statusData.find(s => s.name === 'WITHDRAWN')?.value || 0;
                    const expired = statusData.find(s => s.name === 'EXPIRED')?.value || 0;
                    if (total === 0) return 'N/A';
                    const score = Math.round(((total - withdrawn - expired) / total) * 100);
                    return `${score}%`;
                  })()}
                </KPIValue>
                <KPITrend $positive>Active / Total ratio</KPITrend>
              </KPICard>
            </KPIGrid>
          </div>
        </ContentWrapper>
      </PageContainer>
    </AdminShell>
  );
};

export default Analytics;
